import React, { useState, useEffect, useRef } from 'react';
import './DatingChatBot.css';

const PartnerGenerationChat = ({ 
  isOpen, 
  onClose, 
  onComplete,
  generatedImage
}) => {
  const [messages, setMessages] = useState([]);
  const [currentInput, setCurrentInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const questions = [
    "How old are you?",
    "What is your gender?",
    "What's your ideal partner's gender?",
    "What things do you want to talk to your partner about?",
    "Do you have a favorite color or number?"
  ];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const focusInput = () => {
    if (inputRef.current && currentQuestionIndex < questions.length) {
      inputRef.current.focus();
      const length = inputRef.current.value.length;
      inputRef.current.setSelectionRange(length, length);
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          inputRef.current.setSelectionRange(length, length);
        }
      }, 10);
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Start conversation when chatbot opens
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      // Add first question
      setTimeout(() => {
        setMessages([{
          id: Date.now(),
          type: 'bot',
          content: questions[0]
        }]);
      }, 300);
    }
  }, [isOpen]);

  // Focus input when chatbot opens or question changes
  useEffect(() => {
    if (isOpen && currentQuestionIndex < questions.length) {
      setTimeout(focusInput, 200);
      setTimeout(focusInput, 500);
    }
  }, [isOpen, currentQuestionIndex]);

  // Disable main page scroll and hide navbar when chatbot is open
  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      document.body.classList.add('chatbot-open');
      
      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        document.body.classList.remove('chatbot-open');
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  // Keyboard detection for mobile
  useEffect(() => {
    const handleResize = () => {
      const viewportHeight = window.visualViewport?.height || window.innerHeight;
      const windowHeight = window.innerHeight;
      setIsKeyboardVisible(windowHeight > viewportHeight + 50);
    };

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleResize);
      return () => window.visualViewport.removeEventListener('resize', handleResize);
    }
  }, []);

  const handleClose = (e) => {
    if (e) {
      e.stopPropagation();
    }
    onClose();
  };

  const handleSubmit = async (e) => {
    if (e) {
      e.preventDefault();
    }
    
    if (!currentInput.trim() || isLoading) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: currentInput.trim()
    };

    // Save answer
    const questionKey = questions[currentQuestionIndex].toLowerCase().replace(/[^a-z0-9]+/g, '_');
    setAnswers(prev => ({
      ...prev,
      [questionKey]: currentInput.trim()
    }));

    // Add user message
    setMessages(prev => [...prev, userMessage]);
    setCurrentInput('');
    setIsLoading(true);

    // Check if we have more questions
    if (currentQuestionIndex < questions.length - 1) {
      // Wait a bit for natural flow
      setTimeout(() => {
        const nextIndex = currentQuestionIndex + 1;
        setCurrentQuestionIndex(nextIndex);
        
        // Add next question
        setMessages(prev => [...prev, {
          id: Date.now() + 1,
          type: 'bot',
          content: questions[nextIndex]
        }]);
        
        setIsLoading(false);
        setTimeout(focusInput, 100);
      }, 800);
    } else {
      // All questions answered - close immediately and transition to animation
      setIsLoading(false);
      if (onComplete) {
        onComplete(answers);
      }
      // Close the chat immediately to transition to heart animation
      onClose();
    }
  };


  return (
    <>
      {isOpen && (
        <div className="dating-chatbot-overlay" onClick={handleClose}>
          <div className={`dating-chatbot-container ${isKeyboardVisible ? 'keyboard-visible' : ''}`} onClick={(e) => e.stopPropagation()}>
            <div className="chatbot-header">
              <div className="chatbot-title">
                <div className="chatbot-avatar">
                  <img src="/heartintel.svg" alt="LUV" className="chatbot-logo" />
                </div>
                <div>
                  <h3>Create Your LUV</h3>
                  <p>Your responses train your LUV</p>
                </div>
              </div>
              <button className="chatbot-close" onClick={handleClose}>×</button>
            </div>

            <div className="chatbot-messages">
              {messages.map((message) => (
                <div key={message.id} className={`message ${message.type}`}>
                  <div className="message-content">
                    {message.content}
                  </div>
                  <div className="message-time">
                    {new Date(message.id).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              ))}
              
              {isLoading && (
                <div className="message bot">
                  <div className="message-content">
                    <div className="typing-indicator">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                </div>
              )}

              {generatedImage && currentQuestionIndex >= questions.length && (
                <div className="message bot">
                  <div className="message-content">
                    <img 
                      src={generatedImage.url || generatedImage} 
                      alt="Generated partner" 
                      className="generated-partner-image"
                      loading="eager"
                      fetchPriority="high"
                    />
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {currentQuestionIndex < questions.length && (
              <div className="chatbot-input">
                <div className="input-container">
                  <textarea
                    ref={inputRef}
                    value={currentInput}
                    onChange={(e) => setCurrentInput(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSubmit(e);
                      }
                    }}
                    placeholder="Type your answer here..."
                    rows="2"
                    maxLength="500"
                    disabled={isLoading}
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck="false"
                  />
                </div>
                <div className="input-footer">
                  <div className="progress-bar-container">
                    <div className="progress-bar">
                      <div 
                        className="progress-fill" 
                        style={{ width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                  <div className="input-tip">Press enter to send</div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default PartnerGenerationChat;

